import React from 'react'
import { Landmark } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ToggleSetting } from '@/components/ui/toggle-setting'
import { CASH_BALANCE_SUPPORTED_PLAN_YEAR } from '@/lib/constants'
import { projectStructuredCashBalance } from '@/lib/planDesign'

const CASH_BALANCE_DISCLAIMER = "Planning estimate only. Confirm actual cash-balance credits, section 415(b) limits, testing, and funding with the plan's TPA or actuary."

const DEFAULT_SERVICE_BANDS = [
  { minYears: 0, maxYears: 4, percentOfPay: 3, flatDollar: 0 },
  { minYears: 5, maxYears: 9, percentOfPay: 5, flatDollar: 0 },
  { minYears: 10, maxYears: null, percentOfPay: 7, flatDollar: 0 },
]

function CreditRows({ idPrefix, allocationGroups, credits, memberCounts, onChange, disabled }) {
  const byGroupId = new Map((credits || []).map((credit) => [credit.groupId, credit]))

  const setCredit = (groupId, formula) => {
    const others = (credits || []).filter((credit) => credit.groupId !== groupId)
    onChange(formula ? [...others, { groupId, formula }] : others)
  }

  if (allocationGroups.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        Give employees a group on the Employees step before assigning benefit credits.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {allocationGroups.map((group) => {
        const credit = byGroupId.get(group.id)
        const method = credit?.formula?.method || 'none'
        const formula = credit?.formula || { method: 'percentOfPay', value: 0 }
        const value = formula.value ?? 0
        const count = memberCounts?.[group.id] ?? 0
        return (
          <div key={group.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border bg-background p-2.5">
            <span className="min-w-[9rem] flex-1 text-sm font-medium">
              {group.label}
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                {count} {count === 1 ? 'person' : 'people'}
              </span>
            </span>
            <Select
              value={method}
              onValueChange={(nextMethod) => {
                if (nextMethod === 'none') setCredit(group.id, null)
                else if (nextMethod === 'serviceGraded') {
                  setCredit(group.id, { method: nextMethod, bands: method === nextMethod ? formula.bands : DEFAULT_SERVICE_BANDS })
                } else if (nextMethod === 'permittedDisparity') {
                  setCredit(group.id, method === nextMethod ? formula : {
                    method: nextMethod,
                    basePercent: 3,
                    excessPercent: 2,
                    integrationLevel: null,
                  })
                } else setCredit(group.id, { method: nextMethod, value: method === nextMethod ? value : 0 })
              }}
              disabled={disabled}
            >
              <SelectTrigger aria-label={`${group.label} cash-balance credit`} className="h-8 w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Excluded</SelectItem>
                <SelectItem value="percentOfPay">Percent of pay</SelectItem>
                <SelectItem value="flatDollar">Flat dollar</SelectItem>
                <SelectItem value="serviceGraded">Service-graded</SelectItem>
                <SelectItem value="permittedDisparity">Permitted disparity</SelectItem>
              </SelectContent>
            </Select>
            {method === 'percentOfPay' && (
              <span className="flex items-center gap-1.5">
                <NumberInput
                  id={`${idPrefix}-${group.id}-percent`}
                  aria-label={`${group.label} cash-balance percent of pay`}
                  value={value}
                  onValueChange={(next) => setCredit(group.id, { method, value: Math.min(100, Math.max(0, Number(next) || 0)) })}
                  containerClassName="w-20"
                  className="w-20"
                  min={0}
                  max={100}
                  step="0.25"
                  disabled={disabled}
                />
                <span className="text-sm text-muted-foreground">% of pay</span>
              </span>
            )}
            {method === 'flatDollar' && (
              <span className="flex items-center gap-1.5">
                <span className="text-sm">$</span>
                <NumberInput
                  id={`${idPrefix}-${group.id}-flat`}
                  aria-label={`${group.label} cash-balance flat dollar credit`}
                  value={value}
                  onValueChange={(next) => setCredit(group.id, { method, value: Math.max(0, Number(next) || 0) })}
                  containerClassName="w-28"
                  className="w-28"
                  min={0}
                  step="100"
                  disabled={disabled}
                />
              </span>
            )}
            {method === 'serviceGraded' && (
              <div className="basis-full space-y-1.5 border-t border-border/60 pt-2">
                {(formula.bands || []).map((band, index) => (
                  <div key={`${band.minYears}-${index}`} className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="w-12 text-muted-foreground">Years</span>
                    <NumberInput
                      aria-label={`${group.label} service band ${index + 1} minimum years`}
                      value={band.minYears}
                      onValueChange={(next) => {
                        const bands = formula.bands.map((entry, bandIndex) => bandIndex === index
                          ? { ...entry, minYears: Math.max(0, Number(next) || 0) } : entry)
                        setCredit(group.id, { ...formula, bands })
                      }}
                      containerClassName="w-16"
                      min={0}
                      disabled={disabled}
                    />
                    <span>to</span>
                    <NumberInput
                      aria-label={`${group.label} service band ${index + 1} maximum years`}
                      value={band.maxYears ?? ''}
                      placeholder="up"
                      onValueChange={(next) => {
                        const bands = formula.bands.map((entry, bandIndex) => bandIndex === index
                          ? { ...entry, maxYears: next === '' ? null : Math.max(0, Number(next) || 0) } : entry)
                        setCredit(group.id, { ...formula, bands })
                      }}
                      containerClassName="w-16"
                      min={0}
                      disabled={disabled}
                    />
                    <NumberInput
                      aria-label={`${group.label} service band ${index + 1} percent of pay`}
                      value={band.percentOfPay}
                      onValueChange={(next) => {
                        const bands = formula.bands.map((entry, bandIndex) => bandIndex === index
                          ? { ...entry, percentOfPay: Math.min(100, Math.max(0, Number(next) || 0)) } : entry)
                        setCredit(group.id, { ...formula, bands })
                      }}
                      containerClassName="w-20"
                      min={0}
                      max={100}
                      step="0.25"
                      disabled={disabled}
                    />
                    <span className="text-muted-foreground">% of pay</span>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">The employee&rsquo;s completed service selects one band. Confirm the schedule with the actuary.</p>
              </div>
            )}
            {method === 'permittedDisparity' && (
              <div className="flex basis-full flex-wrap items-end gap-3 border-t border-border/60 pt-2">
                <Label className="space-y-1 text-xs">
                  <span>Base rate</span>
                  <span className="flex items-center gap-1">
                    <NumberInput
                      aria-label={`${group.label} permitted disparity base rate`}
                      value={formula.basePercent ?? 0}
                      onValueChange={(next) => setCredit(group.id, { ...formula, basePercent: Math.max(0, Number(next) || 0) })}
                      containerClassName="w-20"
                      min={0}
                      step="0.25"
                      disabled={disabled}
                    />%
                  </span>
                </Label>
                <Label className="space-y-1 text-xs">
                  <span>Excess rate</span>
                  <span className="flex items-center gap-1">
                    <NumberInput
                      aria-label={`${group.label} permitted disparity excess rate`}
                      value={formula.excessPercent ?? 0}
                      onValueChange={(next) => setCredit(group.id, { ...formula, excessPercent: Math.max(0, Number(next) || 0) })}
                      containerClassName="w-20"
                      min={0}
                      step="0.25"
                      disabled={disabled}
                    />%
                  </span>
                </Label>
                <Label className="space-y-1 text-xs">
                  <span>Integration level</span>
                  <span className="flex items-center gap-1">$
                    <NumberInput
                      aria-label={`${group.label} permitted disparity integration level`}
                      value={formula.integrationLevel ?? ''}
                      placeholder="TWB"
                      onValueChange={(next) => setCredit(group.id, { ...formula, integrationLevel: next === '' ? null : Math.max(0, Number(next) || 0) })}
                      containerClassName="w-28"
                      min={0}
                      step="100"
                      disabled={disabled}
                    />
                  </span>
                </Label>
                <p className="basis-full text-[11px] text-muted-foreground">Blank integration level uses the plan-year taxable wage base. Professional review is required.</p>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function CashBalanceStructuredCard({
  id,
  title,
  description,
  reference,
  planYear,
  design,
  allocationGroups = [],
  memberCounts = {},
  onUpdateDesign,
  disabled = false,
}) {
  const projected = React.useMemo(() => projectStructuredCashBalance(design), [design])
  const supportedYear = Number(planYear) === CASH_BALANCE_SUPPORTED_PLAN_YEAR
  const patchDesign = (patch) => onUpdateDesign({ ...design, ...patch })

  return (
    <div id={id} data-testid="structured-cash-balance-card">
      <Card size="sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span>{title}</span>
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-2">
          {reference}
          <ToggleSetting
            title="Cash balance plan (defined benefit)"
            description="Illustrate explicit annual benefit credits separately from defined-contribution employer cost."
            strong
            checked={design.enabled === true}
            onCheckedChange={(enabled) => patchDesign({ enabled })}
            disabled={disabled || !supportedYear}
          />
          {!supportedYear && (
            <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              Cash-balance modeling is available only for the {CASH_BALANCE_SUPPORTED_PLAN_YEAR} plan year. The credit terms remain saved but inactive.
            </div>
          )}

          {design.enabled && (
            <div className="space-y-3 rounded-md bg-muted/30 p-2.5">
              <section className="space-y-2" aria-labelledby={`${id}-credits-label`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <Label id={`${id}-credits-label`} className="text-sm font-medium">Benefit credits</Label>
                  <span className="text-xs text-muted-foreground">{projected.groups.length} adopted {projected.groups.length === 1 ? 'credit' : 'credits'}</span>
                </div>
                <p className="text-xs text-muted-foreground">Set one annual hypothetical credit for each allocation group.</p>
                <CreditRows
                  idPrefix={id}
                  allocationGroups={allocationGroups}
                  credits={design.credits}
                  memberCounts={memberCounts}
                  onChange={(credits) => patchDesign({ credits })}
                  disabled={disabled}
                />
              </section>

              <section className="space-y-2 border-t border-border/60 pt-2" aria-labelledby={`${id}-eligibility-label`}>
                <Label id={`${id}-eligibility-label`} className="text-sm font-medium">Cash-balance eligibility and entry</Label>
                <p className="text-xs text-muted-foreground">These terms apply only to the cash-balance benefit credit.</p>
                <ToggleSetting
                  title="Use separate cash-balance eligibility"
                  description="When off, the main plan eligibility gate controls the cash-balance credit."
                  checked={design.eligibility?.enabled === true}
                  onCheckedChange={(enabled) => patchDesign({ eligibility: { ...design.eligibility, enabled } })}
                  disabled={disabled}
                />
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <Label className="space-y-1 text-xs">
                    <span>Minimum age</span>
                    <NumberInput
                      aria-label="Cash-balance minimum age"
                      value={design.eligibility?.minAge ?? 21}
                      onValueChange={(minAge) => patchDesign({ eligibility: { ...design.eligibility, enabled: true, minAge: Math.max(0, Number(minAge) || 0) } })}
                      min={0}
                      max={26}
                      disabled={disabled || design.eligibility?.enabled !== true}
                    />
                  </Label>
                  <Label className="space-y-1 text-xs">
                    <span>Service months</span>
                    <NumberInput
                      aria-label="Cash-balance minimum service months"
                      value={design.eligibility?.minServiceMonths ?? 12}
                      onValueChange={(minServiceMonths) => patchDesign({ eligibility: { ...design.eligibility, enabled: true, minServiceMonths: Math.max(0, Number(minServiceMonths) || 0) } })}
                      min={0}
                      disabled={disabled || design.eligibility?.enabled !== true}
                    />
                  </Label>
                  <Label className="space-y-1 text-xs">
                    <span>Minimum hours</span>
                    <NumberInput
                      aria-label="Cash-balance minimum hours"
                      value={design.eligibility?.minHours ?? 1000}
                      onValueChange={(minHours) => patchDesign({ eligibility: { ...design.eligibility, enabled: true, minHours: Math.max(0, Number(minHours) || 0) } })}
                      min={0}
                      step="50"
                      disabled={disabled || design.eligibility?.enabled !== true}
                    />
                  </Label>
                  <Label className="space-y-1 text-xs">
                    <span>Entry dates</span>
                    <Select
                      value={design.eligibility?.entryFrequency ?? 'semiannual'}
                      onValueChange={(entryFrequency) => patchDesign({ eligibility: { ...design.eligibility, enabled: true, entryFrequency } })}
                      disabled={disabled || design.eligibility?.enabled !== true}
                    >
                      <SelectTrigger aria-label="Cash-balance entry frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="semiannual">Semiannual</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </Label>
                </div>
              </section>

              <section className="space-y-2 border-t border-border/60 pt-2" aria-labelledby={`${id}-interest-label`}>
                <Label id={`${id}-interest-label`} className="text-sm font-medium">Interest credit</Label>
                <Select
                  value={design.interestCredit?.type ?? 'fixed'}
                  onValueChange={(type) => patchDesign({
                    interestCredit: type === 'fixed'
                      ? { type: 'fixed', rate: design.interestCredit?.rate ?? design.interestCredit?.assumptionRate ?? 5 }
                      : { type: 'index', index: '30-year-treasury', spread: 0, floor: 0, cap: 6, assumptionRate: design.interestCredit?.rate ?? 5 },
                  })}
                  disabled={disabled}
                >
                  <SelectTrigger aria-label="Cash-balance interest credit type" className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed rate</SelectItem>
                    <SelectItem value="index">Indexed with floor and cap</SelectItem>
                  </SelectContent>
                </Select>
                {design.interestCredit?.type !== 'index' ? (
                  <div className="flex items-center gap-1.5">
                    <NumberInput
                      aria-label="Cash-balance fixed interest credit"
                      value={design.interestCredit?.rate ?? 5}
                      onValueChange={(rate) => patchDesign({ interestCredit: { type: 'fixed', rate: Math.min(6, Math.max(0, Number(rate) || 0)) } })}
                      containerClassName="w-24"
                      min={0}
                      max={6}
                      step="0.25"
                      disabled={disabled}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                    <Label className="space-y-1 text-xs lg:col-span-2">
                      <span>Index</span>
                      <Select
                        value={design.interestCredit.index}
                        onValueChange={(index) => patchDesign({ interestCredit: { ...design.interestCredit, index } })}
                        disabled={disabled}
                      >
                        <SelectTrigger aria-label="Cash-balance interest index"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30-year-treasury">30-year Treasury</SelectItem>
                          <SelectItem value="10-year-treasury">10-year Treasury</SelectItem>
                          <SelectItem value="one-year-treasury">One-year Treasury</SelectItem>
                          <SelectItem value="custom">Other plan index</SelectItem>
                        </SelectContent>
                      </Select>
                    </Label>
                    {[
                      ['floor', 'Floor'],
                      ['cap', 'Cap'],
                      ['assumptionRate', 'Proposal assumption'],
                    ].map(([key, label]) => (
                      <Label key={key} className="space-y-1 text-xs">
                        <span>{label}</span>
                        <span className="flex items-center gap-1">
                          <NumberInput
                            aria-label={`Cash-balance indexed interest ${label.toLowerCase()}`}
                            value={design.interestCredit[key] ?? 0}
                            onValueChange={(next) => patchDesign({ interestCredit: { ...design.interestCredit, [key]: Math.max(0, Number(next) || 0) } })}
                            min={0}
                            step="0.25"
                            disabled={disabled}
                          />%
                        </span>
                      </Label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Results use a 3-year cliff vesting assumption. Indexed projections use the entered proposal assumption and do not predict future index values. This is not required funding or an actuarial valuation.</p>
              </section>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{CASH_BALANCE_DISCLAIMER}</p>
        </CardContent>
      </Card>
    </div>
  )
}
